import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, TextField, Tooltip } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import useTranslation from "../../../Hooks/useTranslation";
import { useBoxes } from "../../../Contexts/BoxContext";
import { getUnitTemplatesByUid } from "../../../Hooks/useUnitTemplates";
import { useUniqueUnitsSearch } from "../../../Hooks/useUniqueUnits";

const isUnitSelectable = (unit) => unit && unit.isEnabled !== false;

const UnitList = ({
  items,
  onCancel,
  onSave,
  submitButtonTitle,
  isTemplate,
  newlyCreatedBoxType,
  onSaveEventLogger,
  onSaveUnit,
  unit,
  licensesNames,
  isLoading,
  section = "units",
}) => {
  const translation = useTranslation();
  const { currentUnit, setCurrentUnitModel } = useBoxes();
  const [search, setSearch] = useState("");
  const [value, setValue] = useState(() => currentUnit);
  const [allowSubmit, setAllowSubmit] = useState(() => !isTemplate);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: searchResults, isFetching: isSearching } = useUniqueUnitsSearch(section, debouncedSearch);

  const filteredItems = useMemo(() => {
    if (search.trim() && debouncedSearch) return searchResults || [];
    return items || [];
  }, [items, search, searchResults, debouncedSearch]);

  const selectedUnit = useMemo(() => {
    if (value == null || !items?.length) return null;
    return items.find((u) => u.id === value) ?? null;
  }, [items, value]);

  const canProceedWithSelectedUnit = isUnitSelectable(selectedUnit);

  useEffect(() => {
    if (!isTemplate) return;
    if (!value) return;
    if (!items) return;
    setAllowSubmit(false);

    const currentUnit = items.find((item) => item.id === value);
    if (!currentUnit) return;
    if (!isUnitSelectable(currentUnit)) return;

    const uId = currentUnit.uId;
    getUnitTemplatesByUid(uId).then((response) => {
      if (response?.length) setAllowSubmit(true);
    });
  }, [value, items, isTemplate]);

  useEffect(() => {
    if (filteredItems.length) {
      const current = filteredItems.find((item) => item.id === value);
      if (!current || !isUnitSelectable(current)) {
        const first = filteredItems.find(isUnitSelectable);
        setValue(first ? first.id : null);
      }
    } else {
      setValue(null);
    }
  }, [filteredItems, search, value]);

  const handleChange = (event) => {
    const value = +event.target.value;
    setValue(value);
  };

  const saveUnit = async () => {
    const unitModel = items.find((u) => u.id === value);
    setCurrentUnitModel(unitModel);
    onSave(value, unitModel);
  };

  const save = (openInNewDashboard = false) => () => {
      let savedData = {};
      const unitModel = items.find((u) => u.id === value);
      savedData = {
        unitUid: unitModel?.uId,
        channels: unitModel?.channels,
      };
      onSaveUnit(savedData, openInNewDashboard)
      onSaveEventLogger(value, unitModel, newlyCreatedBoxType = 'eventLogger');
    };

  return (
    <Fragment>
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        name="search"
        label={translation.dashboardSearchUnitLabel}
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="jobTable jobTable--units">
        <div className="jobTableHead" role="row">
          <span className="jobTableCell jobTableCell--radio" aria-hidden="true" />
          <span className="jobTableCell jobTableCell--name">{translation.headerUnitsLabel}</span>
          <span className="jobTableCell">{translation.dashboardLicenseLabel || 'License'}</span>
          <span className="jobTableCell">UID</span>
          <span className="jobTableCell jobTableCell--date">{translation.lastDataTransmissionLabel}</span>
        </div>
        <div className="jobTableBody">
          <RadioGroup aria-label="units" name="units" value={value != null ? String(value) : ""} onChange={handleChange}>
            {filteredItems.map((item) => {
              const id = String(item.id);
              const disabled = !isUnitSelectable(item);
              const selected = value != null && String(value) === id;
              const licenseName = licensesNames?.find((ln) => ln.licenseId === item.licenseId)?.name || item.licenseId || "—";
              return (
                <label
                  key={item.id}
                  className={`jobTableRow${selected ? " jobTableRow--selected" : ""}${disabled ? " jobTableRow--muted jobTableRow--disabled" : ""}`}
                >
                  <span className="jobTableCell jobTableCell--radio">
                    <Radio value={id} size="small" color="default" disabled={disabled} />
                  </span>
                  <span className="jobTableCell jobTableCell--name" title={item.nameLocalized}>{item.nameLocalized}</span>
                  <span className="jobTableCell jobTableCell--license" title={licenseName}>{licenseName}</span>
                  <span className="jobTableCell jobTableCell--license" title={item.uId}>{item.uId}</span>
                  <span className="jobTableCell jobTableCell--date">{item.lastPointTimestampStr || "—"}</span>
                </label>
              );
            })}
          </RadioGroup>
          {!filteredItems.length && !(isLoading || isSearching) ? (
            <div className="jobTableEmpty">{translation.dashboardNoUnitsLabel || "No units found"}</div>
          ) : null}
        </div>
        {(isLoading || isSearching) && (
          <div className="jobTableLoader">
            <CircularProgress size={28} />
          </div>
        )}
      </div>
      <div className="btnHolder end mt22 fixedWidthBtn">
        <Button onClick={onCancel} variant="outlined">
          {translation.cancelButtonLabel}
        </Button>
        {newlyCreatedBoxType !== "eventLogger" && (
          <Tooltip
            title={
              !value || !allowSubmit || !canProceedWithSelectedUnit
                ? translation.nextButtonTooltipLabel
                : ""
            }
          >
            <span>
              <Button
                onClick={saveUnit}
                disabled={
                  !value || !allowSubmit || !canProceedWithSelectedUnit
                }
                variant="contained"
                color="primary"
                id="unitListSaveBtn"
              >
                {submitButtonTitle || translation.continueButtonLabel}
              </Button>
            </span>
          </Tooltip>
        )}

        {newlyCreatedBoxType === "eventLogger" && (
          <>
          <Button
                    onClick={save(true)}
                    disabled={!value || !canProceedWithSelectedUnit}
                    variant="outlined"
                >
                    {translation.openInNewWindowButtonLabel}
                </Button>
            <Button
              onClick={save()}
              disabled={!value || !canProceedWithSelectedUnit}
              variant="contained"
              color="primary"
            >
              {translation.openButtonLabel}
            </Button>
          </>
        )}
      </div>
    </Fragment>
  );
};

export default UnitList;
